/**
 * Context: OfertaContext
 * Responsibility: Share OfertaCompleta data between pages
 * Following Provider Pattern for state management
 */

import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { OfertaCompleta } from "../models/oferta-personalizada";
import { OfertaContext } from "./ofertaContextDefinition";

interface OfertaProviderProps {
  children: ReactNode;
}

/**
 * Provider para compartir datos de oferta calculada entre páginas
 * Bajo acoplamiento: las páginas se comunican via context
 */
export const OfertaProvider = ({ children }: OfertaProviderProps) => {
  const [oferta, setOferta] = useState<OfertaCompleta | null>(null);
  const [ofertaOriginal, setOfertaOriginalState] = useState<OfertaCompleta | null>(null);

  const handleSetOferta = useCallback((newOferta: OfertaCompleta) => {
    setOferta(newOferta);
  }, []);

  const handleSetOfertaOriginal = useCallback((originalOferta: OfertaCompleta) => {
    setOfertaOriginalState(originalOferta);
  }, []);

  const handleClearOferta = useCallback(() => {
    setOferta(null);
    setOfertaOriginalState(null);
  }, []);

  const updateOfertaPk = useCallback((pk: string) => {
    setOferta((prev) => (prev ? { ...prev, id: pk } : null));
  }, []);

  const contextValue = useMemo(
    () => ({
      oferta,
      ofertaOriginal,
      setOferta: handleSetOferta,
      setOfertaOriginal: handleSetOfertaOriginal,
      clearOferta: handleClearOferta,
      updateOfertaPk,
    }),
    [oferta, ofertaOriginal, handleSetOferta, handleSetOfertaOriginal, handleClearOferta, updateOfertaPk]
  );

  return (
    <OfertaContext.Provider value={contextValue}>
      {children}
    </OfertaContext.Provider>
  );
};
